import { useUserNotifications } from "@/hooks/user/use-user-notifications";
import { Transition } from '@headlessui/react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { Fragment } from 'react'
import { useUserNotificationRead } from "@/hooks/user/use-user-notification-read";
import { useEcho } from "@laravel/echo-react";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from '@tanstack/react-query';
import { useWidgetStore } from '@/widget-store';
import { useNavigate, useLocation } from 'react-router-dom';

export function PushNotifications() {
  const { data, refetch } = useUserNotifications();
  const markRead = useUserNotificationRead();
  const { keycloak, initialized } = useKeycloak();
  const queryClient = useQueryClient();
  const broadcast = useWidgetStore((state) => state.broadcast);
  const navigate = useNavigate();
  const location = useLocation();

  const userUuid = keycloak?.tokenParsed?.sub;
  const isReady = initialized && !!keycloak?.authenticated && !!userUuid;

  const channelName = isReady ? `user.${userUuid}` : null;

  useEcho(
    channelName,
    ".notification",
    (e: any) => {
      refetch();
    },
    [channelName]
  );

  useEcho(
    channelName,
    ".notification.read",
    (e: any) => {
      queryClient.setQueryData(['userNotifications'], (old: any) => {
        if (!old || !Array.isArray(old.notifications)) return old;

        return {
          ...old,
          notifications: old.notifications.filter(
            (n: any) => n.uuid !== e.message.notificationUuid
          ),
        };
      });
    },
    [channelName]
  );

  function handleMarkRead(uuid: string) {
    queryClient.setQueryData(['userNotifications'], (old: any) => {
      if (!old || !Array.isArray(old.notifications)) return old;

      return {
        ...old,
        notifications: old.notifications.filter(
          (n: any) => n.uuid !== uuid
        ),
      };
    });
    markRead.mutateAsync({notificationUuid: uuid});
  }

  function handleLoadObject(notificationUuid: string, objectUuid: string, container: string) {
    queryClient.invalidateQueries(['container_objects']);
    broadcast([{ type: 'ObjectCollection', data: [{ uuid: objectUuid, container }] }]);
    handleMarkRead(notificationUuid);
    if (location.pathname !== '/explore') {
      navigate('/explore');
    }
  }

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 flex flex-col gap-3 px-4 py-6 mt-10 sm:items-end sm:p-6"
      >
        {data?.notifications.map((notification) => (
          <div
            key={notification.uuid}
            className="flex w-[450px] flex-col items-center space-y-4 sm:items-end"
          >
            <Transition
              show={true}
              as={Fragment}
              enter="transform ease-out duration-300 transition"
              enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
              enterTo="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-400" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{notification.title}</p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{notification.message}</p>
                        {notification.type === 'clonedObjectSuccess' && notification.data?.uuid && notification.data?.container && (
                          <div className="mt-3 flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => handleLoadObject(notification.uuid, notification.data!.uuid!, notification.data!.container!)}
                              className="text-sm text-indigo-600 hover:text-indigo-500"
                            >
                              Load
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMarkRead(notification.uuid)}
                              className="text-sm text-gray-900 hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300"
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="flex-shrink-0 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none"
                      onClick={() => handleMarkRead(notification.uuid)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        ))}
      </div>
    </>
  )
}
